import * as React from 'react';
import { useRecoilValue } from 'recoil';
import { useQueries } from '@tanstack/react-query';
import {
  REPORT_01,
  REPORT_02,
  REPORT_03,
  REPORT_04,
  REPORT_05,
  REPORT_06,
  REPORT_07,
  REPORT_08,
  REPORT_09,
  REPORT_Z01,
  REPORT_ST01,
  REPORT_ST02,
  REPORT_ST03,
} from '../Reports/constants';
import RecipientsBox from './RecipientsBox';
import BasicForm from './BasicForm';
import ReportR01 from './ReportR01';
import ReportR02 from './ReportR02';
import ReportR03 from './ReportR03';
import ReportR04 from './ReportR04';
import ReportR05 from './ReportR05';
import ReportR06 from './ReportR06';
import ReportR07 from './ReportR07';
import ReportR08 from './ReportR08';
import ReportR09 from './ReportR09';
import ReportZ01 from './ReportZ01';
import ReportST01 from './ReportST01';
import ReportST02 from './ReportST02';
import ReportST03 from './ReportST03';
import {
  createReport,
  getData,
  getDefaultRecipinet,
  getReportsList,
  updateReport,
} from './AutoReportService';
import { selectedCustomersAtom } from '../../store/recoil/customers';

export interface IReportItem {
  item: string;
  value: string;
  tooltip: string;
  actionUrl: string;
}
interface IProps {
  closeCallback: () => void;
  editingItemId: number | null;
  updateTable: () => void;
}
export default function AllForms(props: IProps): JSX.Element {
  const [selectedReportId, setSelectedReportId] = React.useState<string>('');
  const [reportName, setReportName] = React.useState<string>('');
  const [isActive, setIsActive] = React.useState(1);
  const [reportList, setReportList] = React.useState<IReportItem[]>([]);
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);
  const [recipientValues, setRecipientValues] = React.useState<Record<string, any>[] | null>(null);
  const [editingData, setEditingData] = React.useState<Record<string, any> | null>(null);
  const [havePermissionConflict, setHavePermissionConflict] = React.useState<boolean>(false);
  const handleReportId = (id: string, name: string, active: number) => {
    setSelectedReportId(id);
    setReportName(name);
    setIsActive(active);
  };
  const handleReportsList = async () => {
    const response = await getReportsList();
    if (response) {
      setReportList(response);
    }
  };
  const handleDefaultRecipinet = async () => {
    const response = await getDefaultRecipinet();
    if (response) {
      setRecipientValues([response]);
    }
  };
  const handleData = async () => {
    if (props.editingItemId) {
      const res = await getData(String(props.editingItemId));
      if (res && res.autoReport) {
        if (res.autoReport.recipients) {
          setRecipientValues(res.autoReport.recipients);
        }
        setEditingData(res.autoReport);
        setSelectedReportId(String(res.autoReport?.reportId || ''));
        if (res.havePermissionConflict) {
          setHavePermissionConflict(true);
        }
      }
    }
  };
  const handleAllData = () => {
    if (selectedCustomers && selectedCustomers.length > 0) {
      setSelectedReportId('');
      handleReportsList();
      if (props.editingItemId) {
        handleData();
      } else {
        handleDefaultRecipinet();
      }
    }
  };
  useQueries({
    queries: [
      {
        queryKey: ['autoReportData', selectedCustomers],
        queryFn: () => handleAllData(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    ],
  });
  const saveReport = async (values: Record<string, any>, reportSettingsOptions: string[]) => {
    const newValues = { ...values };
    if (recipientValues && recipientValues.length > 0) {
      if (newValues.frequency === 'weekly') {
        newValues.frequencyParam = values.frequencyParamDaysInWeek;
      } else if (newValues.frequency === 'monthly') {
        newValues.frequencyParam = values.frequencyParamDaysInMonth;
      } else if (newValues.frequency === 'yearly') {
        newValues.frequencyParam = values.frequencyParamMonths;
      }
      delete newValues.frequencyParamDaysInWeek;
      delete newValues.frequencyParamDaysInMonth;
      delete newValues.frequencyParamMonths;
      const recipients = recipientValues.map((i) => {
        return { name: i.name || '', email: i.email, userId: i.userId || null };
      });
      newValues.recipients = recipients;
      newValues.active = String(newValues.active) === '1';
      const reportSettings: { [key: string]: boolean } = {};
      Object.values(reportSettingsOptions).forEach((item: string) => {
        if (item !== 'rideBook') {
          if (newValues?.reportSettings?.includes(item) || newValues?.violations?.includes(item)) {
            reportSettings[item] = true;
          } else {
            reportSettings[item] = false;
          }
        }
      });
      newValues.reportSettings = reportSettings;
      delete newValues.violations;
      if (newValues.showEmpty !== undefined) {
        newValues.showEmpty = String(newValues.showEmpty) === '1';
      }
      const response = editingData
        ? await updateReport(editingData.id, newValues)
        : await createReport(newValues);
      if (response) {
        props.closeCallback();
        if (props.updateTable) {
          props.updateTable();
        }
      }
    }
  };
  const recipientsBox = () => {
    return (
      <RecipientsBox
        recipientValues={recipientValues}
        addingAllowed={!!selectedReportId}
        reportId={selectedReportId}
        handleRecipientValues={setRecipientValues}
      />
    );
  };
  return (
    <div>
      {!selectedReportId && (
        <BasicForm
          reportsList={reportList}
          handleReportId={handleReportId}
          closeCallback={props.closeCallback}
          recipientsBox={recipientsBox()}
          isActive={isActive}
          reportName={reportName}
        />
      )}
      {selectedReportId === REPORT_01 && (
        <ReportR01
          reportsList={reportList}
          handleReportId={handleReportId}
          selectedReportId={selectedReportId}
          editingData={editingData}
          recipientsBox={recipientsBox()}
          updateTable={props.updateTable}
          closeCallback={props.closeCallback}
          saveReport={saveReport}
          isActive={isActive}
          reportName={reportName}
          havePermissionConflict={havePermissionConflict}
        />
      )}
      {selectedReportId === REPORT_02 && (
        <ReportR02
          reportsList={reportList}
          handleReportId={handleReportId}
          selectedReportId={selectedReportId}
          editingData={editingData}
          recipientsBox={recipientsBox()}
          updateTable={props.updateTable}
          closeCallback={props.closeCallback}
          saveReport={saveReport}
          isActive={isActive}
          reportName={reportName}
          havePermissionConflict={havePermissionConflict}
        />
      )}
      {selectedReportId === REPORT_03 && (
        <ReportR03
          reportsList={reportList}
          handleReportId={handleReportId}
          selectedReportId={selectedReportId}
          editingData={editingData}
          recipientsBox={recipientsBox()}
          updateTable={props.updateTable}
          closeCallback={props.closeCallback}
          saveReport={saveReport}
          isActive={isActive}
          reportName={reportName}
          havePermissionConflict={havePermissionConflict}
        />
      )}
      {selectedReportId === REPORT_04 && (
        <ReportR04
          reportsList={reportList}
          handleReportId={handleReportId}
          selectedReportId={selectedReportId}
          editingData={editingData}
          recipientsBox={recipientsBox()}
          updateTable={props.updateTable}
          closeCallback={props.closeCallback}
          saveReport={saveReport}
          isActive={isActive}
          reportName={reportName}
          havePermissionConflict={havePermissionConflict}
        />
      )}
      {selectedReportId === REPORT_05 && (
        <ReportR05
          reportsList={reportList}
          handleReportId={handleReportId}
          selectedReportId={selectedReportId}
          editingData={editingData}
          recipientsBox={recipientsBox()}
          updateTable={props.updateTable}
          closeCallback={props.closeCallback}
          saveReport={saveReport}
          isActive={isActive}
          reportName={reportName}
          havePermissionConflict={havePermissionConflict}
        />
      )}
      {selectedReportId === REPORT_06 && (
        <ReportR06
          reportsList={reportList}
          handleReportId={handleReportId}
          selectedReportId={selectedReportId}
          editingData={editingData}
          recipientsBox={recipientsBox()}
          updateTable={props.updateTable}
          closeCallback={props.closeCallback}
          saveReport={saveReport}
          isActive={isActive}
          reportName={reportName}
          havePermissionConflict={havePermissionConflict}
        />
      )}
      {selectedReportId === REPORT_07 && (
        <ReportR07
          reportsList={reportList}
          handleReportId={handleReportId}
          selectedReportId={selectedReportId}
          editingData={editingData}
          recipientsBox={recipientsBox()}
          updateTable={props.updateTable}
          closeCallback={props.closeCallback}
          saveReport={saveReport}
          isActive={isActive}
          reportName={reportName}
          havePermissionConflict={havePermissionConflict}
        />
      )}
      {selectedReportId === REPORT_08 && (
        <ReportR08
          reportsList={reportList}
          handleReportId={handleReportId}
          selectedReportId={selectedReportId}
          editingData={editingData}
          recipientsBox={recipientsBox()}
          updateTable={props.updateTable}
          closeCallback={props.closeCallback}
          saveReport={saveReport}
          isActive={isActive}
          reportName={reportName}
          havePermissionConflict={havePermissionConflict}
        />
      )}
      {selectedReportId === REPORT_Z01 && (
        <ReportZ01
          reportsList={reportList}
          handleReportId={handleReportId}
          selectedReportId={selectedReportId}
          editingData={editingData}
          recipientsBox={recipientsBox()}
          updateTable={props.updateTable}
          closeCallback={props.closeCallback}
          saveReport={saveReport}
          isActive={isActive}
          reportName={reportName}
          havePermissionConflict={havePermissionConflict}
        />
      )}
      {selectedReportId === REPORT_09 && (
        <ReportR09
          reportsList={reportList}
          handleReportId={handleReportId}
          selectedReportId={selectedReportId}
          editingData={editingData}
          recipientsBox={recipientsBox()}
          updateTable={props.updateTable}
          closeCallback={props.closeCallback}
          saveReport={saveReport}
          isActive={isActive}
          reportName={reportName}
          havePermissionConflict={havePermissionConflict}
        />
      )}
      {selectedReportId === REPORT_ST01 && (
        <ReportST01
          reportsList={reportList}
          handleReportId={handleReportId}
          selectedReportId={selectedReportId}
          editingData={editingData}
          recipientsBox={recipientsBox()}
          updateTable={props.updateTable}
          closeCallback={props.closeCallback}
          saveReport={saveReport}
          isActive={isActive}
          reportName={reportName}
          havePermissionConflict={havePermissionConflict}
        />
      )}
      {selectedReportId === REPORT_ST02 && (
        <ReportST02
          reportsList={reportList}
          handleReportId={handleReportId}
          selectedReportId={selectedReportId}
          editingData={editingData}
          recipientsBox={recipientsBox()}
          updateTable={props.updateTable}
          closeCallback={props.closeCallback}
          saveReport={saveReport}
          isActive={isActive}
          reportName={reportName}
          havePermissionConflict={havePermissionConflict}
        />
      )}
      {selectedReportId === REPORT_ST03 && (
        <ReportST03
          reportsList={reportList}
          handleReportId={handleReportId}
          selectedReportId={selectedReportId}
          editingData={editingData}
          recipientsBox={recipientsBox()}
          updateTable={props.updateTable}
          closeCallback={props.closeCallback}
          saveReport={saveReport}
          isActive={isActive}
          reportName={reportName}
          havePermissionConflict={havePermissionConflict}
        />
      )}
    </div>
  );
}
